import { memo, useMemo } from 'react';

import { ClipStatus, ClipType } from '@air/api/types';
import { Stack } from '@air/next-icons';
import { Button } from '@air/primitive-button';

import {
  RecentlyDeletedTableRow,
  type RecentlyDeletedTableRowProps,
} from '~/components/RecentlyDeleted/components/RecentlyDeletedTableRow';
import { ClipThumbnail } from '~/components/Shared/ClipList/ClipItem/ClipThumbail';
import { DEFAULT_THUMBNAIL_DIMENSIONS } from '~/components/TableView/NameCell/dimensions';
import { ClipIconSize } from '~/types/clip-icon-size';

export type RecentlyDeletedAssetTableRowProps = Pick<
  RecentlyDeletedTableRowProps,
  'dateDeleted' | 'recoveryWindow' | 'deletedBy' | 'style' | 'title' | 'meta'
> & {
  onRecoverAsset: () => void;
  thumbnail?: string;
  deletedFrom?: { title: string; id: string }[] | null;
  ext: string;
  status: ClipStatus;
  versionCount: number;
};

const EMPTY_DELETED_FROM: RecentlyDeletedTableRowProps['deletedFrom'] = [] as const;

export const RecentlyDeletedAssetTableRow = memo(
  ({
    dateDeleted,
    deletedFrom,
    recoveryWindow,
    deletedBy,
    onRecoverAsset,
    style,
    title,
    thumbnail,
    ext,
    status,
    versionCount,
  }: RecentlyDeletedAssetTableRowProps) => {
    const thumbnailEl = useMemo(
      () =>
        thumbnail ? (
          <ClipThumbnail
            iconSize={ClipIconSize.small}
            type={ClipType.photo}
            ext={ext}
            status={status === ClipStatus.transcoded ? status : ClipStatus.nonTranscodable}
            src={thumbnail}
            thumbnailPreference="default"
            {...DEFAULT_THUMBNAIL_DIMENSIONS}
          />
        ) : null,
      [thumbnail, ext, status],
    );

    const actionsEl = useMemo(
      () => (
        <Button appearance="ghost" className="-ml-2.5" onClick={onRecoverAsset}>
          Recover
        </Button>
      ),
      [onRecoverAsset],
    );

    const metaEl = useMemo(
      () =>
        versionCount && versionCount > 1 ? (
          <div className="pointer-events-none flex items-center gap-1">
            <Stack className="size-3" />
            <div className="font-semibold text-12 text-grey-11">{versionCount}</div>
          </div>
        ) : null,
      [versionCount],
    );

    return (
      <RecentlyDeletedTableRow
        actions={actionsEl}
        deletedBy={deletedBy}
        dateDeleted={dateDeleted}
        deletedFrom={deletedFrom ?? EMPTY_DELETED_FROM}
        recoveryWindow={recoveryWindow}
        style={style}
        thumbnail={thumbnailEl}
        title={title}
        meta={metaEl}
        type="asset"
      />
    );
  },
);

RecentlyDeletedAssetTableRow.displayName = 'RecentlyDeletedAssetTableRow';
